import * as HoverCard from "@radix-ui/react-hover-card"
import classNames from "classnames"

import styles from "./ActionButton.module.scss"

import { isValidCancelTypeToSendInvoice } from "@/main/domain/home"
import {
	CancelForListFragment,
	CancelForRereservationFragment,
} from "@/main/gql/__generated__/graphql"
import { isValidPhoneNumber } from "@/utils/phoneNumber"

import { Tab } from "../../HomePage.logic"
import {
	invalidCancelTypeToSendInvoiceMessage,
	invalidPhoneNumberMessage,
} from "../../home.common.logic"
import { showClaimingConversionAppliedStatus } from "../CancelsForRereservationTableView.logic"
import { isClaimingConversion } from "../CancelsTableView.common.logic"

const requestPaymentButtonText = "請求する"
const cancelPaymentButtonText = "取消する"
const cancelledButtonText = "取消済"
const sendMessageButtonText = "送付する"
const stopSendingButtonText = "送付停止"
const updateButtonText = "更新"

type Props = {
	row?: CancelForListFragment
	rereservationRow?: CancelForRereservationFragment
	isEditingRow: boolean
	onClickUpdateButton: () => void
	onClickActionButton: () => void
	currentTab: Tab
}

const getSpanClass = (status: string, errorMessages: string[]): string => {
	if (status === "CLAIM_ABORTED")
		return classNames(styles.background_gray, styles.button_inactive)
	if (errorMessages.length > 0)
		return classNames(styles.background_light_blue, styles.button_inactive)

	switch (status) {
		case "INVOICE_NOT_SENT":
			return classNames(styles.background_dark_orange, styles.button_active)
		case "INVOICE_SENT":
		case "SCHEDULED_TO_CLAIM":
			return classNames(styles.background_dark_red, styles.button_active)
		case "ENCOURAGING_RERESERVATION_NOT_SCHEDULED":
			return classNames(styles.background_dark_orange, styles.button_active)
		case "ENCOURAGING_RERESERVATION_SCHEDULED":
			return classNames(styles.background_dark_red, styles.button_active)
		case "ENCOURAGING_MESSAGE_SENT":
			return classNames(styles.background_dark_red, styles.button_active)
		case "INFORMATION_NOT_ENOUGH":
			return classNames(styles.background_dark_orange, styles.button_active)
		default: //到達しない
			return ""
	}
}

type ActionButtonElems = {
	spanClass: string
	text: string
	errorMessages: string[]
}

const getActionButtonElems = (
	currentTab: Tab,
	claimRow?: CancelForListFragment,
	rereservationRow?: CancelForRereservationFragment
): ActionButtonElems | null => {
	const errorMessages: string[] = []
	if (claimRow && !isValidPhoneNumber(claimRow.phoneNumber)) {
		errorMessages.push(invalidPhoneNumberMessage)
	}
	if (claimRow && !isValidCancelTypeToSendInvoice(claimRow.cancelType)) {
		errorMessages.push(invalidCancelTypeToSendInvoiceMessage)
	}

	if (rereservationRow && !isValidPhoneNumber(rereservationRow.phoneNumber)) {
		errorMessages.push(invalidPhoneNumberMessage)
	}

	const isClaimingConv = rereservationRow
		? isClaimingConversion(rereservationRow)
		: false

	switch (currentTab) {
		case "INVOICE_NOT_SENT":
			if (!claimRow) break
			switch (claimRow.paymentStatus) {
				case "INVOICE_NOT_SENT":
					return {
						spanClass: getSpanClass(claimRow.paymentStatus, errorMessages),
						text: requestPaymentButtonText,
						errorMessages,
					}
				case "SCHEDULED_TO_CLAIM":
					return {
						spanClass: getSpanClass(claimRow.paymentStatus, errorMessages),
						text: cancelPaymentButtonText,
						errorMessages,
					}
				default:
					break
			}
			break

		case "INVOICE_SENT":
			if (!claimRow) break
			switch (claimRow.paymentStatus) {
				case "INVOICE_SENT":
					return {
						spanClass: getSpanClass(claimRow.paymentStatus, errorMessages),
						text: cancelPaymentButtonText,
						errorMessages: errorMessages,
					}
				case "CLAIM_ABORTED":
					return {
						spanClass: getSpanClass(claimRow.paymentStatus, errorMessages),
						text: cancelledButtonText,
						errorMessages: [],
					}
				default:
					break
			}
			break

		case "PAID":
		case "NOT_SUBJECT":
			break

		case "ENCOURAGING_RERESERVATION_NOT_SCHEDULED":
			if (!rereservationRow) break
			switch (rereservationRow.rereservationStatus) {
				case "ENCOURAGING_RERESERVATION_SCHEDULED":
					if (!isClaimingConv) break
					return {
						spanClass: getSpanClass(
							rereservationRow.rereservationStatus,
							errorMessages
						),
						text: stopSendingButtonText,
						errorMessages: errorMessages,
					}

				case "ENCOURAGING_RERESERVATION_NOT_SCHEDULED":
					if (isClaimingConv) break
					return {
						spanClass: getSpanClass(
							rereservationRow.rereservationStatus,
							errorMessages
						),
						text: sendMessageButtonText,
						errorMessages,
					}

				case "INFORMATION_NOT_ENOUGH":
					if (isClaimingConv) break
					return {
						spanClass: getSpanClass(
							rereservationRow.rereservationStatus,
							errorMessages
						),
						text: sendMessageButtonText,
						errorMessages,
					}

				case "CANNOT_ENCOURAGE":
				case "ENCOURAGING_MESSAGE_ABORTED":
				case "ENCOURAGING_MESSAGE_ABORTED_BY_RERESERVED":
					break

				default:
					break
			}
			break

		case "ENCOURAGING_RERESERVATION_SCHEDULED":
			if (!rereservationRow) break
			if (showClaimingConversionAppliedStatus(rereservationRow)) break
			switch (rereservationRow.rereservationStatus) {
				case "ENCOURAGING_MESSAGE_SENT":
					return {
						spanClass: getSpanClass(
							rereservationRow.rereservationStatus,
							errorMessages
						),
						text: stopSendingButtonText,
						errorMessages: errorMessages,
					}
				case "ENCOURAGING_MESSAGE_ABORTED":
				case "ENCOURAGING_MESSAGE_ABORTED_BY_RERESERVED":
					break
				default:
					break
			}

			break
		default:
			break
	}

	return null
}

const ActionButton = (props: Props) => {
	const {
		row,
		rereservationRow,
		isEditingRow,
		onClickUpdateButton,
		onClickActionButton,
		currentTab,
	} = props

	const actionButtonElems = getActionButtonElems(
		currentTab,
		row,
		rereservationRow
	)

	if (actionButtonElems === null) {
		return <span></span>
	}

	const { spanClass, text, errorMessages } = actionButtonElems

	const onClick = () => {
		if (errorMessages.length > 0) {
			return
		}
		onClickActionButton()
	}

	return isEditingRow ? (
		<span
			className={`${styles.background_dark_orange} ${styles.button_active} ${styles.table_elm_background}`}
			onClick={onClickUpdateButton}
		>
			{updateButtonText}
		</span>
	) : (
		// hovercardは共通component作りたい
		<HoverCard.Root openDelay={300}>
			<HoverCard.Trigger>
				<span
					className={`${spanClass} ${styles.table_elm_background}`}
					onClick={onClick}
				>
					{text}
				</span>
			</HoverCard.Trigger>
			{errorMessages.length > 0 && (
				<HoverCard.Portal>
					<HoverCard.Content className={styles.hover_card_content}>
						{errorMessages.map((message, _) => (
							<div>{message}</div>
						))}
						<HoverCard.Arrow className={styles.arrow} />
					</HoverCard.Content>
				</HoverCard.Portal>
			)}
		</HoverCard.Root>
	)
}

export default ActionButton
